import { dramas, type Drama } from "@/mocks/dramas";
import { actors, type Actor } from "@/mocks/actors";
import { reviews, type Review } from "@/mocks/reviews";
import { articles, type Article } from "@/mocks/articles";

export const getDramaById = (id?: string): Drama | undefined =>
  dramas.find((d) => d.id === id);

export const getActorById = (id?: string): Actor | undefined =>
  actors.find((a) => a.id === id);

export const getActorsByIds = (ids: string[]): Actor[] =>
  ids
    .map((id) => getActorById(id))
    .filter((a): a is Actor => Boolean(a));

export const getReviewsByDrama = (dramaId: string): Review[] =>
  reviews.filter((r) => r.dramaId === dramaId);

export const getArticlesByDrama = (dramaId: string): Article[] =>
  articles.filter((a) => a.dramaId === dramaId);

export const getDramasByActor = (actorId: string): Drama[] =>
  dramas.filter((d) => d.castIds.includes(actorId));

export const getRelatedDramas = (drama: Drama): Drama[] =>
  drama.relatedIds
    .map((id) => getDramaById(id))
    .filter((d): d is Drama => Boolean(d));

export const getRatingAverage = (list: Review[]): number => {
  if (list.length === 0) return 0;
  const total = list.reduce((sum, r) => sum + r.rating, 0);
  return Math.round((total / list.length) * 10) / 10;
};

export interface SearchResult {
  dramas: Drama[];
  actors: Actor[];
}

export const searchContent = (query: string): SearchResult => {
  const q = query.trim().toLowerCase();
  if (!q) return { dramas: [], actors: [] };
  const matchedDramas = dramas.filter(
    (d) =>
      d.title.toLowerCase().includes(q) ||
      d.genre.toLowerCase().includes(q) ||
      d.director.toLowerCase().includes(q) ||
      d.tags.some((t) => t.toLowerCase().includes(q))
  );
  const matchedActors = actors.filter(
    (a) =>
      a.name.toLowerCase().includes(q) ||
      a.englishName.toLowerCase().includes(q) ||
      a.signatureRoles.some((r) => r.toLowerCase().includes(q))
  );
  return { dramas: matchedDramas, actors: matchedActors };
};

export const formatViews = (n: number): string => {
  if (n >= 10000) return `${(n / 10000).toFixed(1)} 万`;
  return String(n);
};